import React, { useEffect, useRef, useState } from "react";

/**
 * Reveal, fades and lifts children into view once they enter the viewport.
 * Cinematic, quiet, single-shot (does not re-animate on scroll back up).
 *
 * Props:
 *   as    , HTML tag or React component to render (default: "div")
 *   delay , ms to wait before fading in
 *   className, style, and any extra props are forwarded to the rendered element
 */
export default function Reveal({
  as: Tag = "div",
  delay = 0,
  className = "",
  style,
  children,
  ...rest
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.14, rootMargin: "0px 0px -60px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      style={{ transitionDelay: `${delay}ms`, ...(style || {}) }}
      className={`bnm-reveal ${shown ? "is-visible" : ""} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}
